/**
 * New-bill composer form schema — Zod, react-hook-form resolver source.
 *
 * Mirrors the customer composer pattern (`features/customers/lib/form-defaults.ts`
 * + the per-mode composers under `features/customers/composer/`): a
 * richer-than-the-API form schema that includes UX-only fields
 * (sourceMode, line items, notes) which are derived/dropped at submit time
 * when mapping to `@flint/api-contract` `bills.createInput`.
 *
 * @see AGENTS.md §4 — "Forms | react-hook-form + Zod | Schemas shared with tRPC"
 * @see AGENTS.md §7 rule 7 — "Money is integer paise (`bigint`)"
 */
import { z } from "zod";

// ── Sub-schemas ─────────────────────────────────────────────────────

export const BILL_SOURCE_MODES = ["upload", "email", "manual"] as const;
export type BillSourceMode = (typeof BILL_SOURCE_MODES)[number];

export const EXTRACTION_STATES = ["idle", "extracting", "extracted"] as const;
export type ExtractionState = (typeof EXTRACTION_STATES)[number];

export const BILL_TAX_MODES = ["intra", "inter", "exempt"] as const;
export type BillTaxMode = (typeof BILL_TAX_MODES)[number];

/** Free-form decimal string accepted from <input>, parsed to bigint at submit. */
const decimalString = z
  .string()
  .trim()
  .refine((v) => v === "" || /^\d+(\.\d+)?$/.test(v), {
    message: "Use a decimal number",
  });

const positiveDecimal = decimalString.refine((v) => v !== "" && Number.parseFloat(v) > 0, {
  message: "Must be greater than zero",
});

const percentString = z
  .string()
  .trim()
  .refine((v) => v === "" || /^\d+(\.\d{0,2})?$/.test(v), {
    message: "Use a percent like 18 or 0.10",
  });

export const billLineItemSchema = z.object({
  id: z.string(),
  description: z.string().trim().min(1, "Description is required"),
  hsn: z.string().trim(),
  qty: positiveDecimal,
  unit: z.string().trim().min(1, "Unit is required"),
  rate: positiveDecimal,
  discountRate: percentString.default("0"),
  taxRate: percentString.default("18"),
  priceFlag: z.string().optional(),
});
export type BillLineItem = z.infer<typeof billLineItemSchema>;

// ── Main form schema ────────────────────────────────────────────────

export const billFormSchema = z
  .object({
    sourceMode: z.enum(BILL_SOURCE_MODES).default("upload"),
    extractionState: z.enum(EXTRACTION_STATES).default("idle"),

    vendorId: z.string().min(1, "Choose a vendor"),
    vendorDocNumber: z
      .string()
      .trim()
      .min(1, "Vendor invoice number is required")
      .max(64, "Vendor invoice number is too long"),
    receivedOn: z.string().min(1, "Bill date is required"),
    dueOn: z.string().default(""),
    paymentTerms: z.string().default("Net 15"),
    placeOfSupply: z.string().default(""),
    category: z.string().default(""),
    currency: z.string().default("INR"),
    poNumber: z.string().default(""),
    grnNumber: z.string().default(""),
    taxMode: z.enum(BILL_TAX_MODES).default("intra"),
    rcmApplies: z.boolean().default(false),
    itcEligible: z.boolean().default(true),
    tdsSection: z.string().default("None"),
    tdsRate: percentString.default("0"),
    vendorNote: z.string().default(""),
    internalNote: z.string().default(""),
    approver: z.string().default(""),

    lineItems: z.array(billLineItemSchema).min(1, "Add at least one line item"),
  })
  .superRefine((values, ctx) => {
    if (values.receivedOn && values.dueOn && values.dueOn < values.receivedOn) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dueOn"],
        message: "Due date must be on or after bill date",
      });
    }
  });

/**
 * Form value type — `z.output` so `.default()` fields are required at runtime
 * (matches react-hook-form behaviour when `defaultValues` is supplied).
 * Subcomponents reading `values.paymentTerms` get `string`, not `string | undefined`.
 */
export type BillFormValues = z.output<typeof billFormSchema>;
export type BillFormInput = z.input<typeof billFormSchema>;

// ── Defaults — empty form for the "new bill" path ────────────────────

export const EMPTY_LINE: BillLineItem = {
  id: "line-1",
  description: "",
  hsn: "",
  qty: "1",
  unit: "pcs",
  rate: "",
  discountRate: "0",
  taxRate: "18",
};

export const EMPTY_BILL_FORM: BillFormValues = {
  sourceMode: "upload",
  extractionState: "idle",
  vendorId: "",
  vendorDocNumber: "",
  receivedOn: "",
  dueOn: "",
  paymentTerms: "Net 15",
  placeOfSupply: "",
  category: "",
  currency: "INR",
  poNumber: "",
  grnNumber: "",
  taxMode: "intra",
  rcmApplies: false,
  itcEligible: true,
  tdsSection: "None",
  tdsRate: "0",
  vendorNote: "",
  internalNote: "",
  approver: "",
  lineItems: [EMPTY_LINE],
};
